import { Box, Chip, Skeleton, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { useEffect, useState } from 'react'
import { ChartData, ChartDataPoint, ChartRange } from '../_types/chart-data.type'
import { StockService } from '../services/stock.service'
import { useError } from '../context/error'

interface MiniStockChartProps {
  ticker: string
  initialRange?: ChartRange
}

const RANGE_OPTIONS: ChartRange[] = ['1D', '1M', '1Y', '5Y']

export default function MiniStockChart({ ticker, initialRange = '1M' }: MiniStockChartProps) {
  const { setError } = useError()
  const stockService = new StockService(setError)

  const [range, setRange] = useState<ChartRange>(initialRange)
  const [chartData, setChartData] = useState<ChartData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchChartData()
  }, [ticker, range])

  const fetchChartData = async () => {
    setLoading(true)
    const res = await stockService.getChartData(ticker, range)
    if (res?.chartData) {
      setChartData(res.chartData)
    }
    setLoading(false)
  }

  const calculateGain = (values: ChartDataPoint[]): { gain: number; isPositive: boolean } => {
    if (!values || values.length < 2) return { gain: 0, isPositive: true }
    const firstClose = values[0].close
    const lastClose = values[values.length - 1].close
    const gain = ((lastClose - firstClose) / firstClose) * 100
    return { gain, isPositive: gain >= 0 }
  }

  const { isPositive } = chartData?.values ? calculateGain(chartData.values) : { isPositive: true }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        {RANGE_OPTIONS.map((option) => (
          <Chip
            key={option}
            label={option}
            size="small"
            variant={range === option ? 'filled' : 'outlined'}
            color={range === option ? 'primary' : 'default'}
            onClick={() => setRange(option)}
            sx={{ minWidth: 40 }}
          />
        ))}
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      ) : chartData?.values && chartData.values.length > 0 ? (
        <LineChart
          height={200}
          series={[
            {
              data: chartData.values.map((v) => v.close),
              color: isPositive ? '#4caf50' : '#f44336',
              showMark: false,
              area: true,
              curve: 'linear',
              valueFormatter: (value) => `$${value?.toFixed(2)}`
            }
          ]}
          xAxis={[
            {
              data: chartData.values.map((v) => v.datetime),
              scaleType: 'point',
              tickLabelStyle: { display: 'none' },
              valueFormatter: (value) => value
            }
          ]}
          yAxis={[
            {
              tickLabelStyle: { fontSize: 11 },
              valueFormatter: (value) => `$${value}`
            }
          ]}
          sx={{
            '.MuiAreaElement-root': {
              fillOpacity: 0.2
            },
            '.MuiLineElement-root': {
              strokeWidth: 2.5
            }
          }}
          margin={{ top: 10, bottom: 20, left: 55, right: 10 }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Chart data unavailable
          </Typography>
        </Box>
      )}
    </Box>
  )
}
